'use client';

import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { Slider } from '@/components/ui/slider';
import { getCurrencySymbol } from '@/lib/currency';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import type { RefinementProps } from '@/components/eataly-plp-filters/lib/types';
import {
    formatPriceRefineValue,
    getPriceSliderBounds,
    parsePriceRangeFromRefines,
} from '@/components/eataly-plp-filters/lib/price-facet';

export default function EatalyRefinePrice({
    values,
    attributeId,
    toggleFilter,
    isPending,
}: RefinementProps): ReactElement {
    const location = useLocation();
    const { currency } = useSite();
    const { i18n, t } = useTranslation('categoryPlp');
    const currencySymbol = getCurrencySymbol(i18n.language, currency);

    const { absoluteMin, absoluteMax } = useMemo(() => getPriceSliderBounds(values), [values]);

    const [activeRange, setActiveRange] = useState<[number, number] | null>(null);
    const sliderValues = activeRange || [absoluteMin, absoluteMax];

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const refines = params.getAll(PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
        const parsed = parsePriceRangeFromRefines(refines);

        if (parsed) {
            setActiveRange([parsed.min, parsed.max]);
        } else {
            setActiveRange(null);
        }
    }, [location.search, absoluteMin, absoluteMax]);

    const handleSliderChange = (newValues: number[]) => {
        setActiveRange([newValues[0], newValues[1]]);
    };

    const handleApplyPriceFilter = () => {
        toggleFilter(attributeId, formatPriceRefineValue(sliderValues[0], sliderValues[1]));
    };

    return (
        <div className="flex flex-col gap-4 pt-4">
            <p
                className="flex items-center justify-center gap-2 body-copy-l text-neutral-10"
                aria-label={t(`filtersDrawer.sliderLabel`, {
                    min: `${sliderValues[0]}${currencySymbol}`,
                    max: `${sliderValues[1]}${currencySymbol}`,
                })}>
                <span>
                    {sliderValues[0]} {currencySymbol}
                </span>
                <span>-</span>
                <span>
                    {sliderValues[1]} {currencySymbol}
                </span>
            </p>
            <div
                className={`py-3 px-0.5 ${isPending ? 'pointer-events-none opacity-60' : ''}`}
                onPointerDown={(e) => e.stopPropagation()}>
                <Slider
                    value={sliderValues}
                    onValueChange={handleSliderChange}
                    onValueCommit={handleApplyPriceFilter}
                    min={absoluteMin}
                    max={absoluteMax}
                    step={1}
                    minStepsBetweenThumbs={1}
                    addNumberInput
                />
            </div>
        </div>
    );
}
